import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../store";
import MobileHomeHeroShowcase from "./MobileHomeHeroShowcase";
import {
  MobileHeroBanner,
  MobileShowcaseCard,
  mobileHeroBanners,
  mobileShowcaseCards,
} from "./MobileHomeHeroShowcaseData";
import "./HomeHeroShowcase.css";

export type HeroSlide = {
  id: number | string;
  image: string;
  alt: string;
  link?: string;
};

export type CardItem = {
  id?: string;
  name: string;
  image: string;
  link?: string;
};

export type ShowcaseCard = {
  id: string;
  title: string;
  kicker: string;
  theme: string;
  seeMoreLink: string;
  items: CardItem[];
};

const DUMMY_FALLBACK_IMAGE = "/assets/images/logos/salarlogo.png";

const defaultHeroSlides: HeroSlide[] = [
  {
    id: 1,
    image: "/assets/images/banner/banner-1.jpg",
    alt: "Salar shopping banner",
    link: "/Categories",
  },
  {
    id: 2,
    image: "/assets/images/banner/banner-2.jpg",
    alt: "Salar electronics banner",
    link: "/Categories",
  },
  {
    id: 3,
    image: "/assets/images/banner/banner-3.jpg",
    alt: "Salar fashion banner",
    link: "/Categories",
  },
];

const HomeHeroShowcase: React.FC = () => {
  const navigate = useNavigate();

  const { productsWithDeals, productsWithCoupons } = useSelector(
    (state: RootState) => state.eCommerceProducts,
  );
  const { superCategories } = useSelector((state: RootState) => state.filters);
  const { keepShopping } = useSelector((state: RootState) => state.activity);

  const [activeSlide, setActiveSlide] = useState(0);

  // Auto-cycle hero banner slides
  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % defaultHeroSlides.length);
    }, 6000);

    return () => window.clearInterval(interval);
  }, []);

  // Transform API arrays into 4-item card blocks with sensible fallbacks
  const mapApiToItems = (
    data: any[],
    fallbackItems: CardItem[],
    isCategory: boolean = false,
  ): CardItem[] => {
    if (!data || data.length === 0) return fallbackItems;
    const mapped: CardItem[] = data
      .slice(0, 4)
      .map((item: any, idx: number): CardItem => {
        const fallback = fallbackItems[idx] || fallbackItems[0];
        const name = item.name || item.title || fallback?.name || "";
        const image =
          item.mainImage ||
          item.image ||
          fallback?.image ||
          DUMMY_FALLBACK_IMAGE;
        const link = isCategory
          ? item.name
            ? `/Categories/${encodeURIComponent(item.name)}`
            : "/Categories"
          : item._id
            ? `/DetailPage/${item._id}`
            : fallback?.link || "/Categories";

        return {
          id: item._id || String(idx),
          name,
          image,
          link,
        };
      });

    // Ensure 4 items exist by padding with fallback items if needed
    while (mapped.length < 4 && mapped.length < fallbackItems.length) {
      mapped.push(fallbackItems[mapped.length]);
    }

    return mapped;
  };

  // Filter deals for men fashion or fall back to productsWithCoupons
  const menDeals = (productsWithCoupons || []).filter(
    (p: any) =>
      p.categoryName?.toLowerCase().includes("men") ||
      p.name?.toLowerCase().includes("men") ||
      p.name?.toLowerCase().includes("shirt"),
  );

  // 5 dynamic cards matching old Banner.tsx architecture
  const showcaseCards: ShowcaseCard[] = [
    {
      id: "continue",
      title:
        keepShopping && keepShopping.length > 0
          ? "Pick up where you left off"
          : "Recommended for you",
      kicker: "CONTINUE SHOPPING",
      theme: "blue",
      seeMoreLink:
        keepShopping && keepShopping.length > 0 ? "/buy-again" : "/Categories",
      items: mapApiToItems(keepShopping, mobileShowcaseCards[0].items),
    },
    {
      id: "coupons",
      title: "Keep shopping for",
      kicker: "YOUR RECENT INTERESTS",
      theme: "rose",
      seeMoreLink: "/todays-deals",
      items: mapApiToItems(productsWithCoupons, mobileShowcaseCards[1].items),
    },
    {
      id: "men",
      title: "Deals related to Men Fashion",
      kicker: "TRENDING FOR YOU",
      theme: "sand",
      seeMoreLink: "/Categories/Men",
      items: mapApiToItems(
        menDeals.length > 0 ? menDeals : productsWithCoupons,
        mobileShowcaseCards[2].items,
      ),
    },
    {
      id: "deals",
      title: "Up to 60% off on Groceries",
      kicker: "GROCERY SAVINGS",
      theme: "mint",
      seeMoreLink: "/todays-deals",
      items: mapApiToItems(productsWithDeals, mobileShowcaseCards[3].items),
    },
    {
      id: "categories",
      title: "Categories to explore",
      kicker: "DISCOVER ON SALAR",
      theme: "violet",
      seeMoreLink: "/Categories",
      items: mapApiToItems(
        superCategories.data?.filter((c: any) => c.image) || [],
        mobileShowcaseCards[4].items,
        true,
      ),
    },
  ];

  const handleProductClick = (item: CardItem) => {
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleSeeMoreClick = (link?: string) => {
    if (link) {
      navigate(link);
    }
  };

  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement>,
    fallback: string,
  ) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = fallback || DUMMY_FALLBACK_IMAGE;
  };

  return (
    <>
      {/* Desktop View (> 860px) */}
      <section className="home-showcase home-showcase-desktop">
        <div className="home-showcase-container">
          <div className="home-showcase-hero">
            {defaultHeroSlides.map((slide, index) => (
              <div
                key={slide.id}
                className={`home-showcase-slide ${
                  activeSlide === index ? "active" : ""
                }`}
                onClick={() => slide.link && navigate(slide.link)}
                style={{
                  cursor: slide.link ? "pointer" : "default",
                }}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  loading={index === 0 ? "eager" : "lazy"}
                  onError={(e) => handleImageError(e, DUMMY_FALLBACK_IMAGE)}
                />
              </div>
            ))}

            <div className="home-showcase-hero-fade" />
          </div>

          <div className="home-showcase-cards">
            {showcaseCards.map((card, cardIdx) => (
              <article
                className="home-showcase-card"
                key={card.id || card.title}
              >
                <h3>{card.title}</h3>

                <div className="home-showcase-card-grid">
                  {card.items.map((item, itemIdx) => (
                    <button
                      type="button"
                      className="home-showcase-product"
                      key={item.id || item.name || itemIdx}
                      onClick={() => handleProductClick(item)}
                      title={item.name}
                    >
                      <span className="home-showcase-product-image">
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                          onError={(e) =>
                            handleImageError(
                              e,
                              mobileShowcaseCards[cardIdx]?.items[itemIdx]
                                ?.image || DUMMY_FALLBACK_IMAGE,
                            )
                          }
                        />
                      </span>

                      <strong>{item.name}</strong>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="home-showcase-see-more"
                  onClick={() => handleSeeMoreClick(card.seeMoreLink)}
                >
                  See more
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile View (<= 860px) */}
      <MobileHomeHeroShowcase
        cards={showcaseCards}
        banners={mobileHeroBanners}
      />
    </>
  );
};

export default HomeHeroShowcase;
